import { useEffect, useMemo, useState } from "react";
import dayjs, { Dayjs } from "dayjs";
import { sorted } from "helpers";
import { generatedApi, MonthlySavings } from "src/store/api/generatedApi";
import { useAppDispatch } from "src/store/store";
import {
  getFiscalYearStart,
  getFiscalYearTwoDigit,
  MONTHS_IN_YEAR,
} from "src/utils/fiscalYear";
import { MissionControlTenant } from "src/store/api/generatedApi";

export interface MissionControlTotalSavingsDataPoint {
  value: number;
  label: string;
  fiscalMonth: number;
}

export interface SavingsProjection {
  projectedEndValue: number;
  delta: number;
  status: "hit" | "exceeds" | "misses";
  isComplete: boolean;
}

const HIT_TOLERANCE = 0.02;

export const computeProjection = (
  dataPoints: MissionControlTotalSavingsDataPoint[],
  savingsTarget: number
): SavingsProjection | null => {
  if (savingsTarget <= 0) return null;
  if (dataPoints.length < 2) return null;
  const last = dataPoints[dataPoints.length - 1];
  if (!last || last.fiscalMonth <= 0 || last.fiscalMonth > MONTHS_IN_YEAR) {
    return null;
  }
  const projectedValue = (last.value / last.fiscalMonth) * MONTHS_IN_YEAR;

  const isComplete = last.fiscalMonth === MONTHS_IN_YEAR;
  const delta = projectedValue - savingsTarget;
  if (Math.abs(delta) / savingsTarget <= HIT_TOLERANCE) {
    return {
      projectedEndValue: projectedValue,
      delta: 0,
      status: "hit",
      isComplete,
    };
  }
  if (delta > 0) {
    return {
      projectedEndValue: projectedValue,
      delta,
      status: "exceeds",
      isComplete,
    };
  }
  return {
    projectedEndValue: projectedValue,
    delta: Math.abs(delta),
    status: "misses",
    isComplete,
  };
};

const getFiscalYearMonthLabels = (referenceDate: Dayjs): string[] =>
  Array.from({ length: MONTHS_IN_YEAR }, (_, i) =>
    getFiscalYearStart(referenceDate).add(i, "month").format("MMM YYYY")
  );

const resolveLatestSavingsMonth = (series: MonthlySavings[]): string | null =>
  series.reduce<string | null>(
    (latest, point) =>
      latest === null || point.month > latest ? point.month : latest,
    null
  );

const resolveReferenceDate = (latestSavingsMonth: string | null): Dayjs =>
  latestSavingsMonth === null ? dayjs() : dayjs(latestSavingsMonth);

interface UseMissionControlTotalSavingsResult {
  isLoading: boolean;
  hasData: boolean;
  runningTotalSavings: number;
  dataPoints: MissionControlTotalSavingsDataPoint[];
  fiscalYearLabels: string[];
  projection: SavingsProjection | null;
  fiscalYearTwoDigit: string;
}

interface TenantSavingsResponse {
  savings: MonthlySavings[];
}

const aggregateMonthlySavingsTotals = (
  series: MonthlySavings[]
): { month: string; totalSavings: number }[] => {
  const totalsByMonth = new Map<string, number>();

  series.forEach((point) => {
    totalsByMonth.set(
      point.month,
      (totalsByMonth.get(point.month) ?? 0) + point.total_savings
    );
  });

  return sorted(
    Array.from(totalsByMonth, ([month, totalSavings]) => ({
      month,
      totalSavings,
    })),
    (entry) => entry.month
  );
};

const buildCumulativeDataPoints = (
  monthlyTotals: { month: string; totalSavings: number }[]
): MissionControlTotalSavingsDataPoint[] => {
  return monthlyTotals.reduce<{
    runningTotal: number;
    points: MissionControlTotalSavingsDataPoint[];
  }>(
    (acc, monthPoint) => {
      const cumulativeValue = acc.runningTotal + monthPoint.totalSavings;
      const month = dayjs(monthPoint.month);
      return {
        runningTotal: cumulativeValue,
        points: [
          ...acc.points,
          {
            value: cumulativeValue,
            label: month.format("MMM YYYY"),
            fiscalMonth: month.diff(getFiscalYearStart(month), "month") + 1,
          },
        ],
      };
    },
    { runningTotal: 0, points: [] }
  ).points;
};

export const processSavingsResponses = (
  responses: TenantSavingsResponse[]
): {
  dataPoints: MissionControlTotalSavingsDataPoint[];
  runningTotalSavings: number;
  latestSavingsMonth: string | null;
} => {
  const allSavings = responses.flatMap((response) => response.savings);
  const latestSavingsMonth = resolveLatestSavingsMonth(allSavings);
  const fiscalYearStart = getFiscalYearStart(
    resolveReferenceDate(latestSavingsMonth)
  );
  const fySavings = aggregateMonthlySavingsTotals(allSavings).filter(
    (point) => {
      const month = dayjs(point.month);
      return month.isAfter(fiscalYearStart) || month.isSame(fiscalYearStart);
    }
  );
  const cumulativePoints = buildCumulativeDataPoints(fySavings);
  return {
    dataPoints: cumulativePoints,
    runningTotalSavings:
      cumulativePoints[cumulativePoints.length - 1]?.value ?? 0,
    latestSavingsMonth,
  };
};

export const useMissionControlTotalSavings = (
  tenants: MissionControlTenant[],
  savingsTarget: number
): UseMissionControlTotalSavingsResult => {
  const dispatch = useAppDispatch();
  const [isLoading, setIsLoading] = useState(false);
  const [dataPoints, setDataPoints] = useState<
    MissionControlTotalSavingsDataPoint[]
  >([]);
  const [runningTotalSavings, setRunningTotalSavings] = useState(0);
  const [latestSavingsMonth, setLatestSavingsMonth] = useState<string | null>(
    null
  );
  const referenceDate = useMemo(
    () => resolveReferenceDate(latestSavingsMonth),
    [latestSavingsMonth]
  );
  const fiscalYearLabels = useMemo(
    () => getFiscalYearMonthLabels(referenceDate),
    [referenceDate]
  );

  const tenantNames = useMemo(() => {
    return Array.from(
      new Set(tenants.flatMap((tenant) => tenant.tenant_names))
    );
  }, [tenants]);

  useEffect(() => {
    let isMounted = true;

    const subscriptions = tenantNames.map((tenantName) =>
      dispatch(
        generatedApi.endpoints.getTenantSavings.initiate(
          { tenantName },
          { subscribe: true, forceRefetch: false }
        )
      )
    );

    const loadSavings = async () => {
      if (tenantNames.length === 0) {
        if (!isMounted) return;
        setDataPoints([]);
        setRunningTotalSavings(0);
        setLatestSavingsMonth(null);
        setIsLoading(false);
        return;
      }

      setIsLoading(true);

      const settledResponses = await Promise.all(
        subscriptions.map((subscription) =>
          subscription
            .unwrap()
            .catch((): TenantSavingsResponse => ({ savings: [] }))
        )
      );

      if (!isMounted) return;

      const result = processSavingsResponses(settledResponses);
      setDataPoints(result.dataPoints);
      setRunningTotalSavings(result.runningTotalSavings);
      setLatestSavingsMonth(result.latestSavingsMonth);
      setIsLoading(false);
    };

    void loadSavings();

    return () => {
      isMounted = false;
      subscriptions.forEach((subscription) => {
        subscription.unsubscribe();
      });
    };
  }, [dispatch, tenantNames]);

  const projection = useMemo(
    () => computeProjection(dataPoints, savingsTarget),
    [dataPoints, savingsTarget]
  );

  return {
    isLoading,
    hasData: dataPoints.length > 0,
    runningTotalSavings,
    dataPoints,
    fiscalYearLabels,
    projection,
    fiscalYearTwoDigit: getFiscalYearTwoDigit(referenceDate),
  };
};
